/**
 * Most builders will offer multiple versions of a home that preserve the
 * overall footprint, but change the exterior styling. This is called an
 * elevation. Because it's so common, this is a first class data type in Higharc.
 *
 * @module
 */
import type { Doc } from "building-types/doc";
import type { ElevationCameraPositions, ElevationImages } from "building-types/options/elevationTypes";
import type { DocOption, DocOptionSet, ElevationOptionSet } from "building-types/options/optionSet";
import { type BuiltInSheetCameraKey } from "building-types/views/CameraKey";
import type { Elevation } from "building-types/views/elevationTypes";
import type { Table } from "common/data/table";
import type { CameraPosition } from "common/types/cameraPosition";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import removeEmpty from "common/utils/removeEmpty";
import uid from "common/utils/uid";
import { SystemOptionSet } from "./options/SystemOptionSetEnum";

export const elevationCameraOptionLabels: { [key in BuiltInSheetCameraKey]: string } = {
  defaultExteriorCamera: "Default",
  topLeftPerspectiveCamera: "Top Left Perspective",
  topRightPerspectiveCamera: "Top Right Perspective",
  bottomLeftPerspectiveCamera: "Bottom Left Perspective",
  bottomRightPerspectiveCamera: "Bottom Right Perspective",
};

export const defaultExteriorElevationCamera: CameraPosition = {
  x: 185,
  y: -940,
  z: 65,
  maxPolarAngle: 1.57,
};

export function defaultElevationNew(elevation: Partial<Elevation> = {}): Elevation {
  return {
    id: uid(),
    parentId: SystemOptionSet.ELEVATION_STYLE,
    entityType: "ELEVATION",
    sortIndex: 0,
    name: "Default elevation",
    baseSettings: "Default_01",
    defaultExteriorCamera: defaultExteriorElevationCamera,
    ...elevation,
  };
}

type FixedElevationOptionSetFields = "id" | "optionSetType" | "entityType" | "name" | "sortIndex" | "salesCategory";

export function elevationOptionSetNewDefault(
  optionSet: Omit<ElevationOptionSet, FixedElevationOptionSetFields> = {
    defaultValue: "",
    options: {},
    baseSettings: {},
    cameraPositions: {},
    images: {},
    category: "Elevations",
  },
): ElevationOptionSet {
  return {
    ...optionSet,
    id: SystemOptionSet.ELEVATION_STYLE,
    entityType: "OPTION_SET",
    optionSetType: "ELEVATION",
    sortIndex: 0,
    name: "Elevation",
    tags: ["ELEVATION"],
    salesCategory: SalesCategory.DESIGN_ELEVATIONS,
  };
}

export function copyForElevationOptionSet(
  existing: ElevationOptionSet | undefined,
  elevations: Table<Elevation>,
): ElevationOptionSet {
  const baseSettings: Table<string> = Object.fromEntries(
    Object.entries(elevations).map(([id, { baseSettings }]) => [id, baseSettings]),
  );
  const cameraPositions: Table<ElevationCameraPositions> = Object.fromEntries(
    Object.entries(elevations).map(([id, el]) => [
      id,
      removeEmpty({
        defaultExteriorCamera: el.defaultExteriorCamera,
        topLeftPerspectiveCamera: el.topLeftPerspectiveCamera,
        topRightPerspectiveCamera: el.topRightPerspectiveCamera,
        bottomLeftPerspectiveCamera: el.bottomLeftPerspectiveCamera,
        bottomRightPerspectiveCamera: el.bottomRightPerspectiveCamera,
      }),
    ]),
  );
  const images: Table<ElevationImages> = Object.fromEntries(
    Object.entries(elevations).map(([id, el]) => [
      id,
      removeEmpty({ thumbnail: el.thumbnailUrl, rendered: el.rendered3DImageUrl }),
    ]),
  );

  const elevationIds = Object.keys(elevations);

  /**
   * Preserved verbatim, which is what makes this mirror order-independent: a batch import may carry
   * a `defaultValue` naming an elevation added later in the same batch, and repairing the reference
   * here would latch the first member instead. Referential repair lives in
   * `docRepairElevationDefault`, called from the operations that can orphan the reference. The
   * fallback covers the fresh-doc case, where there is no default yet.
   */
  const defaultValue = existing?.defaultValue || elevationIds[0];

  // Pull off the exact fields that are expected on a DocOption.
  const options: Table<DocOption> = Object.fromEntries(
    Object.entries(elevations).map(([id, el]) => {
      const existingOption = existing?.options?.[id];
      return [
        id,
        {
          id,
          parentId: SystemOptionSet.ELEVATION_STYLE,
          // Match grabbing name from existing option preferentially
          name: existingOption?.name || el.name,
          sortIndex: el.sortIndex,
          entityType: "OPTION",
          ...removeEmpty({
            price: existingOption?.price ?? el.price,
            description: existingOption?.description,
            builderOptionCode: existingOption?.builderOptionCode,
            builderTemplateCode: existingOption?.builderTemplateCode,
            salesImages: existingOption?.salesImages,
            // These don't quite make sense with `doc`.`elevations` as source of truth
            // leaving for now to match previous behavior
            priceTbd: existingOption?.priceTbd,
            disabled: existingOption?.disabled,
          }),
        },
      ];
    }),
  );

  return elevationOptionSetNewDefault({
    defaultValue: defaultValue ?? "",
    options,
    baseSettings,
    cameraPositions,
    images,
    category: existing?.category ?? "Elevations",
    builderOptionSetCode: existing?.builderOptionSetCode,
    readonly: existing?.readonly,
  });
}

export function getElevationOptionSet(optionSets: Table<DocOptionSet>): ElevationOptionSet | undefined {
  if (SystemOptionSet.ELEVATION_STYLE in optionSets) {
    return optionSets[SystemOptionSet.ELEVATION_STYLE] as ElevationOptionSet;
  }
  return undefined;
}

/**
 * Promote a live elevation to default when `defaultValue` names one that is no longer in the table.
 *
 * `copyForElevationOptionSet` used to do this implicitly, which made the mirror order-dependent --
 * a batch import that carried a default naming a later member latched the first member instead.
 * The repair belongs to the operations that can orphan the reference: deleting an elevation, and
 * loading a doc written before this split.
 */
export function docRepairElevationDefault(doc: Doc): Doc {
  const existing = getElevationOptionSet(doc.optionSets);

  if (!existing || existing.defaultValue in doc.elevations) {
    return doc;
  }

  const defaultValue = Object.keys(doc.elevations)[0] ?? "";

  return {
    ...doc,
    optionSets: { ...doc.optionSets, [SystemOptionSet.ELEVATION_STYLE]: { ...existing, defaultValue } },
  };
}

export function docMergeElevationOptionSet(doc: Doc): Doc {
  // Elevations have changed, sync the elevation option set
  const existing = getElevationOptionSet(doc.optionSets);
  const elevationStyle = copyForElevationOptionSet(existing, doc.elevations);

  return {
    ...doc,
    optionSets: { ...doc.optionSets, [SystemOptionSet.ELEVATION_STYLE]: elevationStyle },
  };
}
